import React, { useState } from 'react';
import type { PromptState, UploadedFile, AdvancedOptions, WebsiteData } from '../types';
import { WebsiteHistory } from './WebsiteHistory';
import { ModelSelectorModal } from './ModelSelectorModal';

interface BuilderFormProps {
  onFormSubmit: (formData: PromptState) => void;
  onLoadHistory?: (data: WebsiteData) => void;
  isLoading: boolean;
  error: string | null;
}

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result.split(',')[1]);
      } else {
        reject(new Error('Failed to read blob as base64 string'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const BuilderForm: React.FC<BuilderFormProps> = ({ onFormSubmit, onLoadHistory, isLoading, error }) => {
  const [prompt, setPrompt] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [selectedModel, setSelectedModel] = useState<'gemini' | 'claude'>('gemini');
  const [advancedOptions, setAdvancedOptions] = useState<AdvancedOptions>({
    customCss: '',
    themeOverrides: { primary: '', secondary: '', accent: '' },
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const newUploadedFiles: UploadedFile[] = await Promise.all(
        // FIX: Explicitly type `file` as `File` to avoid it being inferred as `unknown`.
        files.map(async (file: File) => ({
          name: file.name,
          type: file.type,
          base64Data: await blobToBase64(file),
        }))
      );
      setUploadedFiles((prevFiles) => [...prevFiles, ...newUploadedFiles]);
    }
  };

  const removeFile = (fileName: string) => {
    setUploadedFiles(uploadedFiles.filter(file => file.name !== fileName));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) {
      alert('Please describe the website you want to build.');
      return;
    }
    onFormSubmit({ prompt, uploadedFiles, advancedOptions, preferredModel: selectedModel });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-12">
      {/* Model Selector Modal */}
      <ModelSelectorModal
        isOpen={showModelSelector}
        onClose={() => setShowModelSelector(false)}
        selectedModel={selectedModel}
        onSelectModel={setSelectedModel}
      />

      {/* Hero Section */}
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-brand-coral via-purple-500 to-brand-sky bg-clip-text text-transparent mb-3 md:mb-4 px-2">
          Build your dream website
        </h1>
        <p className="text-base md:text-xl text-brand-charcoal/60 dark:text-brand-cream/60 max-w-2xl mx-auto px-4">
          Describe what you want, and watch AI bring it to life
        </p>
      </div>

      {/* Main Input Card - Lovable Style */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="bg-white dark:bg-gray-800 rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Files Preview */}
          {uploadedFiles.length > 0 && (
            <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex flex-wrap gap-2">
                {uploadedFiles.map(file => (
                  <div key={file.name} className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 px-2 md:px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-xs md:text-sm">
                    <svg className="w-3 h-3 md:w-4 md:h-4 text-brand-coral flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="truncate max-w-[100px] md:max-w-[150px] text-gray-700 dark:text-gray-300">{file.name}</span>
                    <button 
                      type="button" 
                      onClick={() => removeFile(file.name)} 
                      className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Main Textarea - Lovable Style */}
          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              disabled={isLoading}
              className="w-full p-4 md:p-6 pb-16 md:pb-20 text-base md:text-lg bg-transparent border-none focus:outline-none resize-none text-brand-charcoal dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:opacity-50"
              placeholder="Ask AJ to create a..."
              style={{ minHeight: '140px' }}
            />
            
            {/* Bottom Action Bar - Lovable Style */}
            <div className="absolute bottom-3 md:bottom-4 left-3 md:left-4 right-3 md:right-4 flex items-center justify-between gap-2 bg-gradient-to-r from-white/80 to-white/80 dark:from-gray-800/80 dark:to-gray-800/80 backdrop-blur-sm rounded-xl p-2 border border-gray-200/50 dark:border-gray-700/50">
              {/* Left Side - Action Buttons */}
              <div className="flex items-center gap-1 md:gap-2">
                {/* File Upload Button */}
                <label className="cursor-pointer p-2 md:p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500 group-hover:text-brand-coral transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </label>

                {/* Advanced Options Toggle */}
                <button 
                  type="button" 
                  onClick={() => setShowAdvanced(!showAdvanced)} 
                  className={`p-2 md:p-2.5 rounded-lg transition-colors ${showAdvanced ? 'bg-brand-coral text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500'}`}
                  disabled={isLoading}
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </button>

                {/* AI Model Selector Button */}
                <button
                  type="button"
                  onClick={() => setShowModelSelector(true)}
                  disabled={isLoading}
                  className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <span>{selectedModel === 'gemini' ? '✨' : '🧠'}</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {selectedModel === 'gemini' ? 'Gemini' : 'Claude'}
                  </span>
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Right Side - Generate Button (Lovable Style - Circular Arrow) */}
              <button
                type="submit"
                disabled={isLoading || !prompt.trim()}
                className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-400 text-white rounded-full transition-all shadow-lg hover:shadow-xl disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0"
                title={isLoading ? 'Generating...' : 'Generate website'}
              >
                {isLoading ? (
                  <svg className="animate-spin h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Advanced Options Panel */}
          {showAdvanced && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Advanced Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  placeholder="Primary Color (e.g., #FF6B6B)"
                  value={advancedOptions.themeOverrides.primary}
                  onChange={(e) => setAdvancedOptions({
                    ...advancedOptions,
                    themeOverrides: { ...advancedOptions.themeOverrides, primary: e.target.value }
                  })}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-coral"
                />
                <input
                  type="text"
                  placeholder="Secondary Color"
                  value={advancedOptions.themeOverrides.secondary}
                  onChange={(e) => setAdvancedOptions({
                    ...advancedOptions,
                    themeOverrides: { ...advancedOptions.themeOverrides, secondary: e.target.value }
                  })}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-coral"
                />
                <input
                  type="text"
                  placeholder="Accent Color"
                  value={advancedOptions.themeOverrides.accent}
                  onChange={(e) => setAdvancedOptions({
                    ...advancedOptions,
                    themeOverrides: { ...advancedOptions.themeOverrides, accent: e.target.value }
                  })}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-coral"
                />
              </div>
              <textarea
                placeholder="Custom CSS (optional)"
                value={advancedOptions.customCss}
                onChange={(e) => setAdvancedOptions({ ...advancedOptions, customCss: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-coral font-mono text-sm"
              />
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
      </form>

      {/* Website History */}
      {!isLoading && (
        <WebsiteHistory onSelectWebsite={(data) => {
          if (onLoadHistory) {
            onLoadHistory(data);
          }
        }} />
      )}

      {/* Suggestions */}
      {!prompt && !isLoading && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Try these examples:</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              'A fitness studio landing page',
              'Portfolio for a UI designer',
              'Restaurant with online menu',
              'Tech startup homepage'
            ].map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setPrompt(example)}
                className="px-4 py-2 text-sm bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full transition-colors text-gray-700 dark:text-gray-300"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};